import { z } from 'zod';
import { InteractionProvider } from '../index.js';
import { InteractionEventType, } from './index.js';
// --- Shared Sub-schemas ---
const SproomStatusDetailsSchema = z.object({
    state: z.string(),
    dateTime: z.string(), // ISO String
    statusCode: z.number(),
    message: z.string().optional(),
    deliveryType: z.string().optional(), // e.g., "autoInvoice", "sproom", "nemhandelEDelivery"
});
const SproomReceiverSchema = z.object({
    cvr: z.string().optional(),
    name: z.string(),
    organizationIdentifier: z.object({
        value: z.string(),
        scheme: z.string(),
    }),
});
// --- Base Data Schema (For successful lifecycle events) ---
// Most events share this structure (Created, TransmissionStarted, Sent, Received, Completed)
const BaseSproomData = z.object({
    companyId: z.string(),
    documentId: z.string(),
    webhookType: z.string(), // e.g., "documentStatusChanged"
    statusDetails: SproomStatusDetailsSchema,
});
// 1. Created Event
export const SproomCreatedEventBronzeSchema = BaseSproomData.extend({
    documentStatus: z.literal('created'),
    statusDetails: SproomStatusDetailsSchema,
});
export const SproomCreatedEventSchema = z.object({
    provider: z.literal(InteractionProvider.Sproom),
    bronzeData: SproomCreatedEventBronzeSchema,
});
// 2. Transmission Started Event
export const SproomTransmissionStartedEventBronzeSchema = BaseSproomData.extend({
    documentStatus: z.literal('transmissionStarted'),
    statusDetails: SproomStatusDetailsSchema,
});
export const SproomTransmissionStartedEventSchema = z.object({
    provider: z.literal(InteractionProvider.Sproom),
    bronzeData: SproomTransmissionStartedEventBronzeSchema,
});
// 3. Sent Event
export const SproomSentEventBronzeSchema = BaseSproomData.extend({
    documentStatus: z.literal('sent'),
    statusDetails: SproomStatusDetailsSchema,
});
export const SproomSentEventSchema = z.object({
    provider: z.literal(InteractionProvider.Sproom),
    bronzeData: SproomSentEventBronzeSchema,
});
// 4. Received Event
// Note: In Sproom logs, "received" (status 401) appears to mean received by the recipient network/VANS
export const SproomReceivedEventBronzeSchema = BaseSproomData.extend({
    documentStatus: z.literal('received'),
    statusDetails: SproomStatusDetailsSchema,
});
export const SproomReceivedEventSchema = z.object({
    provider: z.literal(InteractionProvider.Sproom),
    bronzeData: SproomReceivedEventBronzeSchema,
});
// 5. Transmission Completed Event
export const SproomTransmissionCompletedEventBronzeSchema = BaseSproomData.extend({
    documentStatus: z.literal('transmissionCompleted'),
    statusDetails: SproomStatusDetailsSchema,
});
export const SproomTransmissionCompletedEventSchema = z.object({
    provider: z.literal(InteractionProvider.Sproom),
    bronzeData: SproomTransmissionCompletedEventBronzeSchema,
});
// 6. Transmission Endpoint Not Found Event
export const SproomEndpointNotFoundEventBronzeSchema = BaseSproomData.extend({
    documentStatus: z.literal('endpointNotFound'),
    statusDetails: SproomStatusDetailsSchema,
});
export const SproomEndpointNotFoundEventSchema = z.object({
    provider: z.literal(InteractionProvider.Sproom),
    bronzeData: SproomEndpointNotFoundEventBronzeSchema,
});
// 7. Failed Event
// Note: Structure is significantly different from BaseSproomData
export const SproomFailedEventBronzeSchema = z.object({
    documentStatus: z.literal('failed'),
    receiver: SproomReceiverSchema,
    statusCode: z.number().optional(),
    message: z.string().optional(),
});
export const SproomFailedEventSchema = z.object({
    provider: z.literal(InteractionProvider.Sproom),
    bronzeData: SproomFailedEventBronzeSchema,
});
// 8. sendNemHandelError Event
// Note: Structure is significantly different from BaseSproomData
export const SproomSendNemHandelErrorEventBronzeSchema = BaseSproomData.extend({
    documentStatus: z.literal('sendNemHandelError'),
});
export const SproomSendNemHandelEventSchema = z.object({
    provider: z.literal(InteractionProvider.Sproom),
    bronzeData: SproomSendNemHandelErrorEventBronzeSchema,
});
// --- Mapping Function ---
export const mapSproomEvent = (bronzeEvent) => {
    switch (bronzeEvent.documentStatus) {
        case 'sent':
            // The document has been sent to the network (Peppol/VANS/Email).
            // We map this to Delivered as it has left the sender's control.
            return {
                provider: InteractionProvider.Sproom,
                bronzeData: bronzeEvent,
                type: InteractionEventType.Sent,
                data: {},
            };
        case 'transmissionCompleted':
            // Final confirmation. We also map this to Delivered.
            // Depending on system requirements, you might ignore this if 'sent' is sufficient.
            return {
                provider: InteractionProvider.Sproom,
                bronzeData: bronzeEvent,
                type: InteractionEventType.Delivered,
                data: {},
            };
        case 'created':
            return {
                provider: InteractionProvider.Sproom,
                bronzeData: bronzeEvent,
                type: InteractionEventType.Created,
                data: {},
            };
        case 'sendNemHandelError':
            return {
                provider: InteractionProvider.Sproom,
                bronzeData: bronzeEvent,
                type: InteractionEventType.Failed,
                data: {},
            };
        case 'transmissionStarted':
            return {
                provider: InteractionProvider.Sproom,
                bronzeData: bronzeEvent,
                type: InteractionEventType.TransmissionStarted,
                data: {},
            };
        case 'received':
            return {
                provider: InteractionProvider.Sproom,
                bronzeData: bronzeEvent,
                type: InteractionEventType.Received,
                data: {},
            };
        case 'failed':
            return {
                provider: InteractionProvider.Sproom,
                bronzeData: bronzeEvent,
                type: InteractionEventType.Failed,
                data: {},
            };
        case 'endpointNotFound':
            return {
                provider: InteractionProvider.Sproom,
                bronzeData: bronzeEvent,
                type: InteractionEventType.Failed,
                data: {},
            };
        default:
            // @ts-expect-error
            throw `Unhandled Sproom documentStatus: ${bronzeEvent.documentStatus}`;
    }
};
//# sourceMappingURL=sproom.js.map